import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FanPassConfig, fanPassConfigGet, fanPassConfigRequestList, fanPassConfigSet } from "@/services/firebase/artistHub";
import {
  FAN_PASS_BENEFITS_MAX,
  FAN_PASS_BENEFIT_MAX_CHARS,
  FAN_PASS_PRICE_MAX,
  FAN_PASS_PRICE_MIN,
  FanPassConfigDraft,
  charCount,
  cleanBenefits,
  endOfDayMs,
  toDateInput,
  validateFanPassConfig,
} from "@/lib/fanPass";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import ArtistFanProductSection from "./ArtistFanProductSection";
import { useArtistConfirm } from "./ArtistConfirm";

const fmt = (ms: number | null | undefined) => (ms ? new Date(ms).toLocaleString("zh-TW", { hour12: false }) : "—");
const toDraft = (c: FanPassConfig): FanPassConfigDraft => ({
  enabled: c.enabled,
  priceCoins: c.priceCoins,
  benefits: c.benefits.length ? [...c.benefits] : [""],
  promoEnabled: !!c.promo,
  promoCoins: c.promo?.firstPeriodCoins ?? Math.round(c.priceCoins / 2),
  promoUntil: toDateInput(c.promo?.until ?? null),
});

/**
 * B-23: 鐵粉月卡設定 (contract §1b fanPassConfigGet/Set, staff only).
 * `artistFanPass/{artistId}` missing → backend returns the defaults (enabled:false, 500 coins, 3 benefits).
 * Turning sale on per artist only matters while RC `artist_fans_sale_enabled` is on as well.
 */
export default function ArtistFanPassPanel({ artistId, artistName }: { artistId: string; artistName?: string | null }) {
  const { confirm, dialog } = useArtistConfirm();
  const client = useQueryClient();
  const key = ["artist-fan-pass-config", artistId];
  const config = useQuery({ queryKey: key, queryFn: () => fanPassConfigGet(artistId), retry: false });
  const [draft, setDraft] = useState<FanPassConfigDraft | null>(null);
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState<string[]>([]);
  // C-05: list has no artistId filter (contract v2.7) — one page of pending requests, filtered here. Hidden on error / old backend.
  const pendingRequests = useQuery({
    queryKey: ["fan-pass-requests", "pending", "panel"],
    queryFn: () => fanPassConfigRequestList({ status: "pending", limit: 50 }),
    retry: false,
    staleTime: 60_000,
  });
  const pendingCount = pendingRequests.data?.items.filter((r) => r.artistId === artistId).length ?? 0;

  useEffect(() => {
    if (config.data && draft === null) setDraft(toDraft(config.data));
  }, [config.data, draft]);

  const save = useMutation({
    mutationFn: (d: FanPassConfigDraft) => {
      const until = d.promoEnabled ? endOfDayMs(d.promoUntil) : null;
      return fanPassConfigSet(artistId, {
        enabled: d.enabled,
        priceCoins: d.priceCoins,
        benefits: cleanBenefits(d.benefits),
        promo: d.promoEnabled && until ? { firstPeriodCoins: d.promoCoins, until } : null,
      });
    },
    onSuccess: (saved) => {
      client.setQueryData(key, saved);
      setDraft(toDraft(saved));
      setMessage(`已儲存（${saved.enabled ? "開賣中" : "未開賣"}，每月 ${saved.priceCoins} 金幣）`);
      void client.invalidateQueries({ queryKey: ["artist-audit", artistId] });
    },
  });

  const update = (patch: Partial<FanPassConfigDraft>) => {
    setMessage("");
    setDraft((d) => (d ? { ...d, ...patch } : d));
  };
  const onSave = () => {
    if (!draft || !config.data) return;
    const found = validateFanPassConfig(draft);
    setErrors(found);
    if (found.length) return;
    const before = config.data;
    const lines = [
      `藝人：${artistId}`,
      `開賣：${before.enabled ? "開" : "關"} → ${draft.enabled ? "開" : "關"}`,
      `價格：${before.priceCoins} → ${draft.priceCoins} 金幣／月`,
      `權益：${cleanBenefits(draft.benefits).length} 條`,
      draft.promoEnabled ? `首期優惠：${draft.promoCoins} 金幣（至 ${draft.promoUntil}）` : "首期優惠：無",
    ];
    if (before.priceCoins !== draft.priceCoins) lines.push("", "價格變更後，App 上尚未送出的舊報價會失效（QUOTE_STALE，需重新取得報價）。");
    confirm({ title: "儲存鐵粉月卡設定", message: "確定儲存以下設定？", lines, confirmText: "儲存", onConfirm: () => save.mutate(draft) });
  };

  const busy = save.isPending;
  const loadError = config.isError ? config.error : null;
  const saveError = save.error;
  return (
    <Card className="mb-6" id="fan-pass">
      <CardHeader>
        <CardTitle>鐵粉月卡</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {pendingCount > 0 && (
          <p role="status" className="rounded border border-warning p-3 text-sm">
            此藝人有 {pendingCount} 筆待審的月卡申請（App 自助送出）。
            <Link to={`/creators/fan-pass-requests?artistId=${encodeURIComponent(artistId)}`} className="ml-1 text-primary underline underline-offset-2">
              前往審核
            </Link>
          </p>
        )}
        {/* B-26: IAP per-artist subscription (contract §1c). The coin-mode settings below stay for RC artist_fan_pass_payment = "coin". */}
        <ArtistFanProductSection artistId={artistId} artistName={artistName} />
        <h3 className="font-medium">月卡設定（開賣開關、權益；金幣價格只在金幣制使用）</h3>
        <p className="text-sm text-muted-foreground">
          粉絲以「購買金幣」開通該藝人 1 個月鐵粉權益，預設自動續期。全站開賣仍由 Remote Config <code>artist_fans_sale_enabled</code> 控制；
          這裡的開關只決定此藝人是否開賣。已開通的月卡不受開關影響，照常續期與到期。
        </p>
        {config.isPending && <p>讀取設定…</p>}
        {loadError && (
          <p role="alert" className="text-destructive">
            讀取失敗：{loadError instanceof Error ? loadError.message : "請重試"}
          </p>
        )}
        {config.data?.isDefault && <p className="text-sm text-amber-700">尚未設定，以下為預設值（儲存後才會建立設定）。</p>}
        {draft && (
          <>
            <div className="flex items-center gap-3">
              <Switch id="fan-pass-enabled" checked={draft.enabled} disabled={busy} onCheckedChange={(v) => update({ enabled: v })} />
              <Label htmlFor="fan-pass-enabled">{draft.enabled ? "此藝人開賣中" : "此藝人未開賣"}</Label>
            </div>
            <div className="max-w-xs">
              <Label htmlFor="fan-pass-price">
                每月價格（金幣，{FAN_PASS_PRICE_MIN}–{FAN_PASS_PRICE_MAX}）
              </Label>
              <Input
                id="fan-pass-price"
                type="number"
                inputMode="numeric"
                min={FAN_PASS_PRICE_MIN}
                max={FAN_PASS_PRICE_MAX}
                step={1}
                value={Number.isFinite(draft.priceCoins) ? draft.priceCoins : ""}
                disabled={busy}
                onChange={(e) => update({ priceCoins: e.target.value === "" ? NaN : Number(e.target.value) })}
              />
              <p className="mt-1 text-xs text-muted-foreground">只收購買金幣；免費金幣不能付月卡。</p>
            </div>
            <div className="space-y-2">
              <Label>
                權益文案（最多 {FAN_PASS_BENEFITS_MAX} 條，每條 ≤{FAN_PASS_BENEFIT_MAX_CHARS} 字；App 開通面板依序顯示）
              </Label>
              {draft.benefits.map((b, i) => {
                const over = charCount(b.trim()) > FAN_PASS_BENEFIT_MAX_CHARS;
                return (
                  <div key={i} className="flex items-center gap-2">
                    <Input
                      aria-label={`權益第 ${i + 1} 條`}
                      value={b}
                      disabled={busy}
                      aria-invalid={over}
                      className={over ? "border-destructive" : undefined}
                      onChange={(e) => update({ benefits: draft.benefits.map((x, j) => (j === i ? e.target.value : x)) })}
                    />
                    <span className={`w-14 shrink-0 text-right text-xs ${over ? "text-destructive" : "text-muted-foreground"}`}>
                      {charCount(b.trim())}/{FAN_PASS_BENEFIT_MAX_CHARS}
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={busy || draft.benefits.length <= 1}
                      onClick={() => update({ benefits: draft.benefits.filter((_, j) => j !== i) })}
                    >
                      移除
                    </Button>
                  </div>
                );
              })}
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={busy || draft.benefits.length >= FAN_PASS_BENEFITS_MAX}
                onClick={() => update({ benefits: [...draft.benefits, ""] })}
              >
                新增一條
              </Button>
            </div>
            <div className="space-y-2 rounded border p-3">
              <div className="flex items-center gap-3">
                <Switch id="fan-pass-promo" checked={draft.promoEnabled} disabled={busy} onCheckedChange={(v) => update({ promoEnabled: v })} />
                <Label htmlFor="fan-pass-promo">首期優惠（選填；只適用第一次開通的第一期）</Label>
              </div>
              {draft.promoEnabled && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="fan-pass-promo-coins">首期金幣</Label>
                    <Input
                      id="fan-pass-promo-coins"
                      type="number"
                      inputMode="numeric"
                      min={1}
                      step={1}
                      value={Number.isFinite(draft.promoCoins) ? draft.promoCoins : ""}
                      disabled={busy}
                      onChange={(e) => update({ promoCoins: e.target.value === "" ? NaN : Number(e.target.value) })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="fan-pass-promo-until">優惠截止日（含當日）</Label>
                    <Input id="fan-pass-promo-until" type="date" value={draft.promoUntil} disabled={busy} onChange={(e) => update({ promoUntil: e.target.value })} />
                  </div>
                </div>
              )}
            </div>
            {errors.length > 0 && (
              <ul role="alert" className="list-disc pl-5 text-sm text-destructive">
                {errors.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            )}
            {saveError && (
              <p role="alert" className="text-destructive">
                儲存失敗：{saveError instanceof Error ? saveError.message : "請重試"}
              </p>
            )}
            {message && <p role="status">{message}</p>}
            <div className="flex flex-wrap items-center gap-3">
              <Button disabled={busy} onClick={onSave}>
                {busy ? "儲存中…" : "儲存月卡設定"}
              </Button>
              <Button
                variant="outline"
                disabled={busy || !config.data}
                onClick={() => {
                  if (config.data) setDraft(toDraft(config.data));
                  setErrors([]);
                  setMessage("");
                }}
              >
                還原
              </Button>
              {config.data && !config.data.isDefault && (
                <span className="text-xs text-muted-foreground">
                  上次更新 {fmt(config.data.updatedAt)}
                  {config.data.updatedBy ? `（${config.data.updatedBy}）` : ""}
                </span>
              )}
            </div>
          </>
        )}
      </CardContent>
      {dialog}
    </Card>
  );
}
